import 'ol/ol.css';

import Map from 'ol/Map';
import Tile from 'ol/layer/Tile';
import View from 'ol/View';
import {Attribution, defaults as defaultControls} from 'ol/control';
import {DragPan} from 'ol/interaction';
import {Viewer} from "../main";
import {OSM, XYZ} from "ol/source";
import {transform} from 'ol/proj';
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import {Point, Polygon} from "ol/geom";
import {Feature, MapBrowserEvent} from "ol";
import {boundingExtent} from 'ol/extent';
import LocalScene, {
  DataProjectionCoordinate,
  LonLatCoordinate,
  SceneCoordinate
} from '../projections';
import {Vector3} from 'three';
import { Projection } from '../../redux/projections-slice';
import { OrthoMosaicList } from '.';
import { CameraFeature, CameraImage } from '../cameras';
import { AerialViewState } from '../../redux/camera-slice';
import onresize from "resize-event";
import { showContextMenu } from '../../context-menu';
import { clearAerialTooltip, setAerialState } from '../viewer';
import { ChangeDetector } from '../utilities';
import { CustomMouseEvent } from '../controls';
import { emptyBasemapID } from '../../types/basemaps';

const CreateBasemapLayer = (id: string, source) => {
  return new Tile({
    source: source,
    properties: {
      id: id,
      type: 'basemap'
    }
  });
};

export class TagFeature extends Feature<any> {
  public tagID: string;
  public texture: string;
  public size: number;

  constructor(tagID, texture, size, opts) {
    super(opts);
    this.tagID = tagID;
    this.size = size;
    this.texture = texture;
  }
}

class ViewTriangle {
  private viewer: Viewer;
  private aerialMap: AerialMap;
  private feature;
  private layer;

  constructor(aerialMap: AerialMap, viewer: Viewer) {
    this.viewer = viewer;
    this.aerialMap = aerialMap;

    this.generateSource();
  }

  get map() {
    return this.aerialMap.map;
  }

  get camera() {
    return this.viewer.camera;
  }

  generateSource() {
    // Adding our view triangle source
    const source = new VectorSource({
      wrapX: false
    });

    const polygon = new Polygon([[[0,0], [0,0]]]);
    const feature = new Feature(polygon);
    source.addFeature(feature);

    const layer = new VectorLayer({
      zIndex: 2,
      visible: false,
      source: source,
      properties: {
        type: "triangle"
      },
      style: new Style({
        stroke: new Stroke({
          color: "rgba(0,0,0,0.5)",
          width: 1.0
        }),
        fill: new Fill({
          color: "rgba(64,244,155,0.5)"
        })
      })
    });

    this.map.addLayer(layer);
    this.feature = feature;
    this.layer = layer;
  }

  rotateViewMarker(lookat) {
    // Updating feature geometry
    let ring = this.generateMarkerCoordinates(lookat);
    let polygon = new Polygon([ring]);
    polygon.transform('EPSG:4326', 'EPSG:3857');
    this.feature.setGeometry(polygon);
  }

  generateMarkerCoordinates(lookat) {
    // Set up additional constants
    let height = this.map.getTargetElement().clientHeight;
    let coneAngle = 0.8 * 0.5 * (this.camera.fov * Math.PI / 180);
    let axis = new Vector3(0, 0, 1);

    // Cone length is measured in meters based on aerial height
    let meterPerPixel = this.map.getView().getResolution();
    let coneLength = height * 0.10 * meterPerPixel;
    let maxConeLength = 1000000;
    if (coneLength > maxConeLength) {
      coneLength = 0;
    }

    // Center position
    let v0 = this.camera.position;

    // Set triangle size
    let vector = new Vector3(lookat.x, lookat.y, 0);
    vector.setLength(coneLength);

    // First triangle corner
    let v1 = vector.clone()
      .applyAxisAngle(axis, coneAngle)
      .add(v0);

    // Second triangle corner
    let v2 = vector.clone()
      .applyAxisAngle(axis, -coneAngle)
      .add(v0);

    v0 = new SceneCoordinate(v0).toLonLat();
    v1 = new SceneCoordinate(v1).toLonLat();
    v2 = new SceneCoordinate(v2).toLonLat();

    let ring = [
      [v1.x, v1.y],
      [v2.x, v2.y],
      [v0.x, v0.y],
      [v1.x, v1.y]
    ];

    return ring;
  }

  clear() {
    this.layer.setVisible(false);
  }

  update(changeDetector: ChangeDetector) {
    if (!LocalScene.initialized || !changeDetector.changed) return;

    // Update view marker geometry
    this.rotateViewMarker(this.viewer.lookatVector);
    this.layer.setVisible(true);
  }
}

export class AerialMap {
  private viewer: Viewer;
  public containerElement;
  public map: Map;
  public orthoList: OrthoMosaicList;
  private viewTriangle: ViewTriangle;
  private maxLayerZoom = 20;
  private maxMapZoomLevel = 28;
  private maxCameraZoomLevel = 23;
  private markerRadius = 6;
  private observer;

  private currentCameraFeature: Feature;
  private currentCameraLayer: VectorLayer<any>;
  private selectedCameraFeature: Feature;
  private selectedCameraLayer: VectorLayer<any>;

  public mapSystem = {
    string: "EPSG:3857",
    transform: null
  } as Projection;

  constructor(viewer: Viewer, container) {
    this.viewer = viewer;
    this.containerElement = container;

    this.initMap();
    this.initEvents();
    this.initResize();

    this.addCurrentLayer();
    this.addSelectedLayer();

    this.orthoList = new OrthoMosaicList(this);
  }

  get defaultLayers() {
    const arcGISBase = "https://services.arcgisonline.com/ArcGIS/rest/services";

    const layers = [
      CreateBasemapLayer("open-street-maps", new OSM({
        maxZoom: this.maxLayerZoom
      })),

      CreateBasemapLayer("arcgis-world", new XYZ({
        url: `${arcGISBase}/World_Imagery/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),

      CreateBasemapLayer("arcgis-world", new XYZ({
        attributions: 'Powered by <a href="https://www.esri.com/en-us/home" target="_blank">Esri</a>',
        url: `${arcGISBase}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),

      CreateBasemapLayer("arcgis-street", new XYZ({
        attributions: 'Powered by <a href="https://www.esri.com/en-us/home" target="_blank">Esri</a>',
        url: `${arcGISBase}/World_Street_Map/MapServer/tile/{z}/{y}/{x}.png`,
        maxZoom: this.maxLayerZoom
      })),
    ];

    return layers;
  }

  get cameraHandler() {
    return this.viewer.cameraHandler;
  }

  get width() {
    return this.containerElement.scrollWidth;
  }

  get height() {
    return this.containerElement.scrollHeight;
  }

  get basemapLayers() {
    return this.map
      .getLayers()
      .getArray()
      .filter(layer => layer.get("type") === "basemap");
  }

  get activeBasemap() {
    let basemapID = null;
    this.basemapLayers.forEach(layer => {
      let isVisible = layer.getVisible();
      if (isVisible) {
        basemapID = layer.get("id");
      }
    });

    return basemapID;
  }

  get aerialState() {
    const view = this.map.getView();

    const activeBasemap = this.basemapLayers
      .find(x => x.getVisible());

    const basemap = activeBasemap
      ? activeBasemap.get("id")
      : emptyBasemapID;

    return {
      center: view.getCenter(),
      zoom: view.getZoom(),
      rotation: view.getRotation(),
      basemap
    } as AerialViewState;
  }

  get anyToolEnabled() {
    return this.orthoLabels.enabled
      || this.measurements.enabled;
  }

  get orthoLabels() {
    return this.viewer.imageLabels.orthoLabels;
  }

  get localAligner() {
    return this.viewer.controls.localAligner;
  }

  get measurements() {
    return this.viewer.measurements;
  }

  get tagsHandler() {
    return this.viewer.tagsHandler;
  }

  get element() {
    return this.map.getTargetElement();
  }

  resetView(state = {} as AerialViewState) {
    const view = this.map.getView();

    const center = state.center
      ? state.center
      : [0, 0];

    const zoom = state.zoom
      ? state.zoom
      : 0;

    const rotation = state.rotation
      ? state.rotation
      : 0;

    const basemap = state.basemap;

    view.setCenter(center);
    view.setZoom(zoom);
    view.setRotation(rotation);
    this.setVisibleBasemap(basemap);
  }

  initMap() {
    const attribution = new Attribution({
      collapsible: true,
    });

    this.map = new Map({
      layers: this.defaultLayers,
      target: this.containerElement,
      controls: defaultControls({
        zoom: false,
        attribution: false,
        rotate: true
      }).extend([attribution]),
      view: new View({
        maxZoom: this.maxMapZoomLevel
      })
    });

    this.viewTriangle = new ViewTriangle(this, this.viewer);

    this.resetView();
    this.setCurrentCamera(null, false);
  }

  initResize() {
    this.observer = onresize(this.containerElement, () => {
      this.handleWindowResize();
    });

    this.handleWindowResize();
  }

  initEvents() {
    this.onMapClick = this.onMapClick.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.updateSavedState = this.updateSavedState.bind(this);

    this.map.on('click', this.onMapClick);
    this.map.on('dblclick', this.onDoubleClick);
    this.map.on('pointermove', this.onMouseMove);
    this.map.on('moveend', this.updateSavedState);
    this.element.addEventListener('mouseup', this.onMouseUp, false);
  }

  removeEvents() {
    this.map.un('click', this.onMapClick);
    this.map.un('dblclick', this.onDoubleClick);
    this.map.un('pointermove', this.onMouseMove);
    this.map.un('moveend', this.updateSavedState);
    this.element.removeEventListener('mouseup', this.onMouseUp, false);
  }

  addCurrentLayer() {
    const style = new Style({
      image: new CircleStyle({
        radius: this.markerRadius,
        stroke: new Stroke({
          color: "#000000"
        }),
        fill: new Fill({
          color: "#DD0C29"
        }),
      })
    });

    this.currentCameraFeature = new Feature({
      geometry: new Point([0,0])
    });

    const source = new VectorSource({
      wrapX: false,
      features: [this.currentCameraFeature]
    });

    this.currentCameraLayer = new VectorLayer({
      zIndex: 2,
      visible: false,
      source,
      style
    });

    this.map.addLayer(this.currentCameraLayer);
  }

  addSelectedLayer() {
    const style = new Style({
      image: new CircleStyle({
        radius: this.markerRadius,
        stroke: new Stroke({
          color: "#000000"
        }),
        fill: new Fill({
          color: "#FFC300"
        }),
      })
    });

    this.selectedCameraFeature = new Feature({
      geometry: new Point([0,0])
    });

    const source = new VectorSource({
      wrapX: false,
      features: [this.selectedCameraFeature]
    });

    this.selectedCameraLayer = new VectorLayer({
      zIndex: 2,
      visible: false,
      source,
      style
    });

    this.map.addLayer(this.selectedCameraLayer);
  }

  onDoubleClick(event) {
    this.orthoLabels.onDoubleClick(event);
  }

  onMapClick(event) {
    const mapClicked = this.orthoLabels.onMapClick(event)
      || this.localAligner.onMapClick(event)
      || this.tagsHandler.onMapClick(event);

    if (mapClicked) return;

    const features = this.tagsHandler.hovering
      ? this.tagsHandler.getFeatureForEvent(event)
      : this.cameraHandler.getFeatureForEvent(event);

    // No features were found
    if (features.length === 0) return;

    const currentView = this.map.getView();
    const currentZoom = Math.ceil(currentView.getZoom());
    const maxZoomReached = currentZoom >= this.maxCameraZoomLevel;
    const singleFeature = features.length === 1;

    if (singleFeature || maxZoomReached) {
      const feature = features[0];

      if (feature instanceof TagFeature) {
        const selectedTagID = feature.tagID;
        this.viewer.showTagDetails(selectedTagID);
      } else if (feature instanceof CameraFeature) {
        const selectedCameraID = feature.camera.id;
        this.viewer.loadImage(selectedCameraID);
      }

      return;
    }

    // Zoom to cluster with 50% boundary
    const coordinates = [...features]
      .map((x: Feature<any>) => x.getGeometry().getCoordinates());
    const extent = boundingExtent(coordinates);
    const mapSize = this.map.getSize();

    this.map.getView().fit(extent, {
      size: [mapSize[0] * 0.5, mapSize[1] * 0.5],
      nearest: false,
      duration: 250,
    });

    clearAerialTooltip();
  }

  onMouseUp(initialEvent) {
    const event = this.getCustomEvent(initialEvent);

    const mouseClicked = this.orthoLabels.onMouseUp(event)
      || this.localAligner.onMouseUp(event)
      || this.measurements.onMouseUpAerial(event)
      || this.tagsHandler.onMouseUpAerial(event);

    if (mouseClicked) return;

    if (event.isRightClick) {
      this.openContextMenu(event);
    }
  }

  onMouseMove(event: MapBrowserEvent<UIEvent>) {
    if (this.orthoLabels.enabled) {
      this.orthoLabels.onMouseMove(event);
    } else if (this.measurements.enabled) {
      this.measurements.onMouseMoveAerial(event);
    } else if (this.localAligner.enabled) {
      this.localAligner.onMouseMoveAerial();
    } else {
      this.tagsHandler.onMouseMoveAerial(event);
    }

    if (event.dragging) return;
    this.onFeatureHover(event);
  }

  onFeatureHover(event) {
    const cameras = this.cameraHandler.getFeatureForEvent(event);
    const selectedCamera = cameras[0];

    if (selectedCamera) {
      const geometry = selectedCamera.getGeometry();
      this.selectedCameraFeature.setGeometry(geometry);
      this.selectedCameraLayer.setVisible(true);
    } else {
      this.selectedCameraLayer.setVisible(false);
    }
  }

  getCustomEvent(event) : CustomMouseEvent {
    event.isLeftClick = event.button === 0;
    event.isRightClick = event.button === 2;
    return event;
  }

  updateSavedState() {
    setAerialState(this.aerialState);
  }

  openContextMenu(event) {
    if (this.anyToolEnabled) {
      return showContextMenu(event, {
        isAerial: true
      });
    }

    // Tag attributes
    const pixel = this.map.getEventPixel(event);
    const tags = this.tagsHandler.getFeatureForEvent({pixel});
    const tagIDs = tags.map(tag => tag.tagID);

    // New tag coordinate
    const mapCoordinate = this.map.getCoordinateFromPixel(pixel);
    const lonlat = transform(mapCoordinate, 'EPSG:3857', 'EPSG:4326');
    const aerialCoord = new LonLatCoordinate(lonlat)
      .toDataProjection()
      .toArray()
      .slice(0,2);

    // Show default context menu
    showContextMenu(event, {
      isAerial: true,
      selectedTags: tagIDs,
      aerialCoord
    });
  }

  handleWindowResize() {
    if (!this.map) return;
    this.map.updateSize();
    this.map.render();
  }

  setDragState(state) {
    this.map.getInteractions().forEach(interaction => {
      if (interaction instanceof DragPan) {
        interaction.setActive(state);
      }
    });
  }

  jumpToPosition(position) {
    const view = this.map.getView();
    view.setCenter(position);
  }

  setVisibleBasemap(basemapID: string) {
    basemapID = basemapID || emptyBasemapID;

    this.basemapLayers.forEach(layer => {
      let id = layer.get("id");
      layer.setVisible(id === basemapID);
    });

    this.updateSavedState();
  }

  setCurrentCamera(camera: CameraImage, centerOnImage: boolean) {
    if (this.currentCameraLayer) {
      this.currentCameraLayer.setVisible(false);
    }

    if (!camera) return;

    const mapPosition = camera.position.aerial;
    const point = new Point([mapPosition[0], mapPosition[1]]);

    this.currentCameraFeature.setGeometry(point);

    // Move aerial map to camera position
    if (centerOnImage) {
      this.jumpToPosition(mapPosition);
    }

    this.currentCameraLayer.setVisible(true);
  }

  setMapExtent(extent) {
    const view = this.map.getView();

    view.setRotation(0);
    view.fit(extent, {
      size: this.map.getSize()
    });
    view.setZoom(view.getZoom() - 1.0);
  }

  destroy() {
    this.removeEvents();
    this.observer.disconnect();
    this.map.setTarget(null);
  }

  update(changeDetector: ChangeDetector) {
    if (this.viewer.orbitState) {
      this.viewTriangle.clear();
      return;
    }

    if (this.viewer.hasPoints || this.viewer.cameraSelected) {
      this.viewTriangle.update(changeDetector);
    }
  }
}

export const extentFromPoints = (positions, reproject=true) => {
  let xPositions = [];
  let yPositions = [];

  if (reproject) {
    positions = positions.map(position => {
      let lonlat = new DataProjectionCoordinate(position).toLonLat();
      let mapPos = transform([lonlat.x, lonlat.y], 'EPSG:4326', 'EPSG:3857');
      return mapPos;
    });
  }

  positions.forEach(position => {
    xPositions.push(position[0]);
    yPositions.push(position[1]);
  });

  let extent = [
    Math.min(...xPositions),
    Math.min(...yPositions),
    Math.max(...xPositions),
    Math.max(...yPositions)
  ];

  return extent;
};